import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Inbox, Loader2, RefreshCcw, Send, User as UserIcon, ArrowLeft,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Sol menü için WhatsApp Sohbetleri sayfası.
 * Sadece Gelen Kutusu + Thread + Yanıt kutusu — ayarlar/test/loglar
 * "Ayarlar → WhatsApp" sekmesinde kalıyor.
 */
export default function WhatsAppPage() {
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const loadConversations = async () => {
    setConvLoading(true);
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setConversations(data.items || []);
    } catch (e) {
      // İlk kullanımda WhatsApp yapılandırılmamış olabilir — sessiz geç
      console.warn(e);
    } finally { setConvLoading(false); }
  };

  const openThread = async (phone) => {
    setSelectedPhone(phone);
    setThreadLoading(true);
    try {
      const { data } = await api.get(
        `/whatsapp/conversations/${encodeURIComponent(phone)}/messages`
      );
      setThread(data.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setThreadLoading(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedPhone) return;
    setReplying(true);
    try {
      const { data } = await api.post(
        `/whatsapp/conversations/${encodeURIComponent(selectedPhone)}/reply`,
        { text: replyText }
      );
      if (data.dry_run) toast.info(data.detail);
      else if (data.sent) toast.success("Yanıt gönderildi");
      else toast.error(data.error_detail || "Gönderilemedi");
      setReplyText("");
      await openThread(selectedPhone);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplying(false); }
  };

  useEffect(() => { loadConversations(); }, []);

  return (
    <div className="space-y-5">
      <PageHeader
        title="WhatsApp Sohbetleri"
        subtitle="Gelen kutusu ve müşteri yanıtları"
        breadcrumbs={["Ana Sayfa", "WhatsApp"]}
        actions={
          <Button
            onClick={loadConversations}
            disabled={convLoading}
            variant="outline"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
            data-testid="wa-refresh-btn"
          >
            {convLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Yenile
          </Button>
        }
      />

      <Card className="rounded-sm border-slate-800 bg-slate-950/40 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] min-h-[calc(100vh-260px)]">
          {/* Sol: kişi listesi */}
          <div className="border-r border-slate-800 max-h-[calc(100vh-240px)] overflow-y-auto">
            <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2 bg-slate-900/50 sticky top-0 z-10">
              <Inbox className="w-4 h-4 text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-slate-400 font-mono">Gelen Kutusu</span>
              <span className="ml-auto text-[10px] font-mono text-slate-500">{conversations.length}</span>
            </div>
            {convLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm space-y-2">
                <MessageCircle className="w-8 h-8 mx-auto text-slate-700" />
                <div>Henüz mesaj yok</div>
                <div className="text-[11px] text-slate-600">
                  Müşteri size WhatsApp'tan mesaj yazdığında burada görünecek.
                </div>
              </div>
            ) : conversations.map((c) => (
              <button
                key={c.phone}
                data-testid={`wa-conv-${c.phone}`}
                onClick={() => openThread(c.phone)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-900 hover:bg-slate-900/60 transition-colors ${
                  selectedPhone === c.phone ? "bg-slate-900/80" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <UserIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    {c.profile_name && (
                      <div className="text-sm font-semibold text-slate-100 truncate">{c.profile_name}</div>
                    )}
                    <div className={`${c.profile_name ? "text-[10px] text-slate-500" : "text-sm font-semibold text-slate-100"} font-mono truncate`}>
                      {c.phone}
                    </div>
                  </div>
                  {c.unread > 0 && (
                    <span className="ml-auto text-[9px] uppercase font-mono bg-emerald-500 text-emerald-950 px-1.5 py-0.5 rounded-sm">
                      {c.unread}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  <span className={c.last_direction === "in" ? "text-emerald-500" : "text-slate-500"}>
                    {c.last_direction === "in" ? "→" : "←"}
                  </span>{" "}
                  {c.last_text}
                </div>
                {c.last_at && (
                  <div className="text-[10px] text-slate-600 font-mono mt-0.5">
                    {new Date(c.last_at).toLocaleString("tr-TR")}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Sağ: thread */}
          <div className="flex flex-col max-h-[calc(100vh-240px)]">
            {!selectedPhone ? (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6">
                <div className="text-center space-y-2">
                  <MessageCircle className="w-10 h-10 mx-auto text-slate-700" />
                  <div>Sol taraftan bir sohbet seçin</div>
                </div>
              </div>
            ) : threadLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-slate-900/40 sticky top-0 z-10">
                  <button
                    onClick={() => setSelectedPhone(null)}
                    className="md:hidden text-slate-400 hover:text-slate-100"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <UserIcon className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-mono font-semibold text-slate-100">{selectedPhone}</span>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-950/30">
                  {thread.length === 0 && (
                    <div className="text-center text-xs text-slate-500 py-8">Mesaj yok</div>
                  )}
                  {thread.map((m, i) => (
                    <div
                      key={i}
                      data-testid={`wa-msg-${i}`}
                      className={`flex ${m.direction === "in" ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-sm px-3 py-1.5 text-sm ${
                          m.direction === "in"
                            ? "bg-slate-800 text-slate-100"
                            : m.dry_run
                            ? "bg-amber-950/40 border border-amber-800 text-amber-100"
                            : m.ok
                            ? "bg-emerald-700 text-emerald-50"
                            : "bg-rose-900 text-rose-100"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className="text-[9px] mt-1 opacity-70 font-mono">
                          {m.at && new Date(m.at).toLocaleString("tr-TR")}
                          {m.dry_run && " · DRY-RUN"}
                          {m.error_detail && ` · ${m.error_detail}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-800 p-2 flex gap-2 bg-slate-950/40">
                  <Input
                    data-testid="wa-reply-input"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    placeholder="Yanıt yazın... (24 saatlik pencere içinde serbest metin)"
                    className="bg-slate-950 border-slate-700 rounded-sm text-sm flex-1"
                  />
                  <Button
                    data-testid="wa-reply-send-btn"
                    onClick={sendReply}
                    disabled={replying || !replyText.trim()}
                    className="bg-emerald-600 hover:bg-emerald-500 rounded-sm"
                  >
                    {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
